import React, { Dispatch, SetStateAction, useState } from "react";

import { faCaretRight, faMobile } from "@fortawesome/pro-solid-svg-icons";
import { useSelector } from "react-redux";

import { MultiButton } from "components/Button";
import { GroupedThirdPartyCard } from "components/BuyerCards/BuyerCards";
import { Mobile3x1Layout } from "components/Layout/ContentHeader";
import { SearchHeader } from "components/SearchInput/GlobalSearch";

import { Settings } from "constants/settings";

import {
  getActiveRole,
  getIsOnline,
  getSaleLotGroupsByThirdPartyBuyerAndBuyerWayForBusinessUser,
  getSetting,
  getSettings,
  prepareBusinessReportData,
} from "selectors";

import { useGetBusinessUserReportLinksByAgencyName } from "hooks/useGetBusinessUserReportLinksByAgencyName";
import { ParentSaleTabType } from "constants/sale";
import { EMPTY_OBJECT } from "lib";
import { buildBusinessUserSubNavReportButtons } from "./buildReportButtons";

interface BusinessUserBuyerCardsComponentProps {
  setIsMobile: Dispatch<SetStateAction<boolean>>;
}

export const BusinessUserBuyerCards = ({
  setIsMobile,
}: BusinessUserBuyerCardsComponentProps): React.JSX.Element => {
  const [quickFilterText, setQuickFilterText] = useState("");
  const thirdPartyBuyers = useSelector(
    getSaleLotGroupsByThirdPartyBuyerAndBuyerWayForBusinessUser,
  );

  const onQuickFilterChange = value => {
    setQuickFilterText(value);
  };

  const isOnline = useSelector(getIsOnline);

  const agencyId: number | null =
    useSelector(state => getSettings(state)[Settings.agencyId]) || null;

  const {
    links,
  }: {
    links: {
      buyer: ReportLink[];
      vendor: ReportLink[];
    };
  } = useSelector(prepareBusinessReportData);

  const parentSaleTab: ParentSaleTabType = useSelector(
    getSetting(Settings.parentSaleTab),
  );

  const reportLinksByAgencyName: Record<string, MinifiedReportLink[]> =
    useGetBusinessUserReportLinksByAgencyName(links, parentSaleTab);

  const activeRole: BusinessUserRole = useSelector(getActiveRole);

  const isCommercialUser: boolean = activeRole.is_commercial_user;

  const additionalActionButtions: MultiButtonActionButton[] = React.useMemo(
    () => [
      {
        title: "Reports",
        subNavItems: buildBusinessUserSubNavReportButtons(
          reportLinksByAgencyName,
        ),
        default: true,
        isDisabled: !isOnline,
        icon: faCaretRight,
      },
      {
        title: "Desktop View",
        icon: faMobile,
        onClick: () => {
          setIsMobile(false);
        },
        dataTour: "desktopView",
      },
    ],
    [isOnline, reportLinksByAgencyName, setIsMobile],
  );

  const actionButton = React.useMemo(
    () => <MultiButton buttons={additionalActionButtions} />,
    [additionalActionButtions],
  );

  return (
    <>
      <SearchHeader
        showFacetedSearch={false}
        showBasicSearch
        onBasicSearchTextChanged={onQuickFilterChange}
        actionButton={actionButton}
        layout={Mobile3x1Layout}
      />
      {thirdPartyBuyers.map(thirdParty => (
        <GroupedThirdPartyCard
          key={`gtpc-${thirdParty.id}`}
          searchText={quickFilterText.toLowerCase()}
          agencyId={agencyId || null}
          filterValues={EMPTY_OBJECT}
          showCommercialData={isCommercialUser}
          thirdParty={thirdParty}
        />
      ))}
    </>
  );
};
